import React from 'react';
import noop from 'lodash/noop';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import {
  ProviderContextDTO,
} from 'dtos';
import PCPInputs from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics/PCPInputs';
import {
  useUpdatePatientChart,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  normalizeDemographicPayload,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  useNotify,
} from 'core/hooks';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';

export type PCPDialogParams = {
  primaryCarePhysician: ProviderContextDTO;
  hasNoPrimaryCarePhysician?: boolean;
  patient: PatientCompact;
};

type Props = PCPDialogParams & {
  onClose: () => void;
};

type PrimaryCarePhysicianForm = {
  primaryCarePhysician: ProviderContextDTO;
  hasNoPrimaryCarePhysician?: boolean;
};

export default function PCPDialog({
  primaryCarePhysician,
  hasNoPrimaryCarePhysician,
  patient,
  onClose,
}: Props) {
  const notify = useNotify();
  const { mutateAsync: updatePatientChart } = useUpdatePatientChart();

  const formProps = useForm<PrimaryCarePhysicianForm>({
    mode: 'all',
    defaultValues: {
      primaryCarePhysician: primaryCarePhysician ?? {},
      hasNoPrimaryCarePhysician,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = formProps;

  const onSubmit = async (formData: PrimaryCarePhysicianForm) => {
    try {
      await updatePatientChart({
        ...normalizeDemographicPayload(formData),
        patientId: patient?.patientId ?? 0,
        // Hack to prevent clearing middle name as sending null means remove middle name.
        middleName: patient?.middleName,
      });

      notify({
        variant: 'success',
        message: 'Primary Care Physician saved successfully!',
      });

      visitNoteEventChannel.emit('onRelatedInfoUpdate', { patientId: patient?.patientId ?? 0 });
      onClose?.();
    } catch (e: unknown) {
      console.error(e);
    }
  };

  return (
    <Dialog
      open
      title="Edit Primary Care Physician"
      onClose={onClose}
      onConfirm={noop}
      className="!w-[620px]"
    >
      <FormProvider {...formProps}>
        <div className="flex flex-1 flex-col gap-form-row populate-form min-h-[8rem] w-full justify-between">
          <div className="flex flex-col gap-form-row w-full">
            <PCPInputs
              classes={{
                contacts: 'w-full',
                dateContainer: '!w-full',
              }}
              shouldStackContact
            />
          </div>
          <div className="flex flex-row justify-end items-center gap-2">
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isValid}
              onClick={handleSubmit(onSubmit)}
            >
              {getSubmitButtonText(false, isSubmitting)}
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
